import ShopSection from './components/ShopSection'
import { useState } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'

function App() {
  const [activeTab, setActiveTab] = useState('home')

  // Navigation items
  const navItems: Array<{id: string; label: string}> = []

  // Other destinations for cross-linking
  const destinations = [
    { name: 'HelloXinjiang', url: 'https://helloxinjiang.pages.dev', emoji: '🏔️', description: 'Xinjiang Beauty' },
    { name: 'HelloLasa', url: 'https://hellolasa.pages.dev', emoji: '🕌', description: 'Tibetan Culture' },
    { name: 'HelloYichang', url: 'https://helloyichang.pages.dev', emoji: '🌊', description: 'Three Gorges' },
    { name: 'Wudang Time Chamber', url: 'https://wudangtimechamber.com', emoji: '🧘', description: 'Sacred Mountain Retreat for Men' }
  ]

  // Main attractions for Tibet (Xizang)
  const attractions = [
    {
      id: 1,
      title: 'Mount Everest Base Camp',
      description: 'The world\'s highest mountain, with base camps offering breathtaking views of the peak.',
      image: '🏔️',
      features: ['Trekking routes', 'Base camp views', 'High-altitude', 'Photography']
    },
    {
      id: 2,
      title: 'Potala Palace',
      description: 'The iconic winter palace of the Dalai Lama, a UNESCO World Heritage site in Lhasa.',
      image: '🏰',
      features: ['UNESCO site', 'Historical artifacts', 'Religious significance', 'Architecture']
    },
    {
      id: 3,
      title: 'Yamdrok Lake',
      description: 'One of Tibet\'s three sacred lakes, known for its stunning turquoise waters.',
      image: '🏞️',
      features: ['Sacred lake', 'Turquoise waters', 'Pilgrimage site', 'Scenic drive']
    },
    {
      id: 4,
      title: 'Tashilhunpo Monastery',
      description: 'The traditional seat of the Panchen Lama, founded in 1447 in Shigatse.',
      image: '🛕',
      features: ['Important monastery', 'Panchen Lama seat', 'Historical significance', 'Buddhist art']
    }
  ]

  // Travel tips
  const travelTips = [
    {
      title: 'Altitude Acclimatization',
      description: 'Spend several days acclimatizing to Tibet\'s high altitude before strenuous activities.',
      icon: '🏔️'
    },
    {
      title: 'Travel Permits',
      description: 'Foreign visitors need Tibet Travel Permits arranged through authorized tour operators.',
      icon: '📝'
    },
    {
      title: 'Cultural Respect',
      description: 'Respect local customs, religious sites, and always ask permission before photographing people.',
      icon: '🙏'
    },
    {
      title: 'Weather Preparation',
      description: 'Tibet\'s weather can change rapidly; bring layers and protection from sun and wind.',
      icon: '🌤️'
    }
  ]

  return (
    <>
      <SEO 
        title="Discover Xizang - The Roof of the World"
        description="Your comprehensive guide to Xizang (Tibet) tourism, attractions, culture, and travel tips. Explore Himalayas, Tibetan Buddhist monasteries, and more."
        keywords="Xizang, Tibet, travel, tourism, attractions, Himalayas, Tibetan culture,西藏,旅游,景点,喜马拉雅,藏族文化"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Google-style Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg font-bold">XZ</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">HelloXizang</h1>
                <p className="text-gray-600 text-sm">Discover Mystical Tibet</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === item.id
                      ? 'bg-indigo-50 text-indigo-600 font-medium'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Contact Button */}
            
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-50 to-purple-100">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-indigo-500">📍</span>
              <span className="text-sm font-medium">Tibet Autonomous Region</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Discover <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Mystical Tibet</span><br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Roof of the World</span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-10 max-w-2xl">
              Experience the spiritual heart of the Himalayas, where ancient monasteries perch on mountain slopes, 
              prayer flags flutter in the wind, and breathtaking landscapes stretch to the horizon. Tibet offers 
              a journey of spiritual discovery and natural wonder.
            </p>

            
          </div>
        </div>
      </section>

      <ShopSection />

      {/* Free Tickets Promotion */}
      <section className="py-12 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full mb-6">
              <span className="text-white text-2xl">🎫</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Free <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Tibet Attraction Tickets</span>
            </h2>
            <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
              Get up to 5 attraction tickets per person by sending an email
            </p>
            <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
                <div className="text-3xl">📧</div>
                <div className="text-left">
                  <p className="text-gray-800 font-medium">Email to Get Tickets</p>
                  <a 
                    href="mailto:info@helloxizang.com" 
                    className="text-lg font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
                  >
                    info@helloxizang.com
                  </a>
                </div>
                <a 
                  href="mailto:info@helloxizang.com"
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md"
                >
                  Get Tickets Now
                </a>
              </div>
            </div>
            <p className="text-gray-600 text-sm mt-6">
              Limited tickets available, first come first served. Valid for Tibet attractions only.
            </p>
          </div>
        </div>
      </section>

      {/* News Section */}
      <NewsSection 
        website="helloxizang"
        limit={10000}
        title="Tibet Travel News"
        themeColor="indigo"
      />

      {/* Attractions Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sacred <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Destinations</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover the spiritual and natural wonders of Tibet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {attractions.map((attraction) => (
              <div key={attraction.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="text-4xl mb-4">{attraction.image}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{attraction.title}</h3>
                  <p className="text-gray-600 mb-4">{attraction.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {attraction.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-gray-700">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Travel <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Tips</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Essential information for your Tibet journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-3xl mb-4">{tip.icon}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{tip.title}</h4>
                <p className="text-gray-600">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Touch</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              For travel inquiries and assistance with your Tibet journey
            </p>
            
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
              <div className="text-4xl mb-4">📧</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Email</h3>
              <p className="text-gray-700 text-lg mb-6">
                Reach out to us for travel information and assistance
              </p>
              
              <div className="inline-flex items-center justify-center space-x-3 bg-white rounded-xl px-6 py-3.5 shadow-sm">
                <span className="text-indigo-500 text-xl">✉️</span>
                <a 
                  href="mailto:info@helloxizang.com" 
                  className="text-xl font-semibold text-gray-900 hover:text-indigo-600 transition-colors duration-200"
                >
                  info@helloxizang.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-lg font-bold">XZ</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">HelloXizang</h3>
                  <p className="text-gray-400 text-sm">Tibet Travel Guide</p>
                </div>
              </div>
              <p className="text-gray-400">
                Your comprehensive guide to discovering the spiritual and natural wonders of Tibet, the Roof of the World.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Contact</h4>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <span>📧</span>
                  <a href="mailto:info@helloxizang.com" className="hover:text-white transition-colors duration-200">
                    info@helloxizang.com
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  For travel information and assistance with your Tibet journey
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Explore More Destinations</h4>
              <ul className="space-y-3">
                {destinations.map((destination) => (
                  <li key={destination.name}>
                    <a 
                      href={destination.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      <span className="text-lg">{destination.emoji}</span>
                      <span>
                        <span className="font-medium">{destination.name}</span>
                        <span className="block text-sm text-gray-500">{destination.description}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 HelloXizang. All rights reserved. | Tibet Travel Information Portal</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}

export default App